<script setup lang="ts">
import { vIntersectionObserver } from "@vueuse/components";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VStack from "@magnit/core/src/components/VStack/VStack.vue";
import VStackItem from "@magnit/core/src/components/VStack/VStackItem.vue";
import { useAnalyticManager } from "@magnit/analytic-events";
import {
  linkHotline,
  phoneAntiCorruption,
  phoneForCustomers,
} from "./footer.vars";

interface IFooterContact {
  title: string;
  number: string;
}

const numbers: IFooterContact[] = [
  {
    title: `Для покупателей <br/>и контрагентов`,
    number: phoneForCustomers,
  },
  {
    title: `Антикоррупционная <br/>горячая линия`,
    number: phoneAntiCorruption,
  },
];

// Analytics
const { send } = useAnalyticManager();
const onVisibility = ([{ isIntersecting }]: IntersectionObserverEntry[]) => {
  if (isIntersecting) {
    send("Footer:Feedback:View", {
      text_1: numbers[0].title.replace(/[<br/>]/g, ""),
      text_2: numbers[1].title.replace(/[<br/>]/g, ""),
    });
  }
};

const onClickNumber = (item: IFooterContact) => {
  send("Footer:Feedback:Phone:Click", {
    text: item.title.replace(/[<br/>]/g, ""),
  });
};
</script>

<template>
  <VStack
    v-intersection-observer="onVisibility"
    class="unit-footer-contacts"
    direction="row"
    :gap="{ xs: '8px', s: '12px' }"
  >
    <VStackItem :size="{ xs: 12 }">
      <VText font="title-small">Служба поддержки</VText>
    </VStackItem>
    <VStackItem :size="{ xs: 12 }">
      <VStack direction="row" :gap="{ xs: '8px', s: '12px', l: '24px' }">
        <VStackItem
          v-for="item in numbers"
          :key="item.number"
          :size="{ xs: 6, l: 'auto' }"
        >
          <VText
            tag="div"
            :font="{
              xs: 'caption-large-regular-low',
              ml: 'body-small-regular',
              l: 'caption-large-regular-low',
            }"
            color="secondary"
          >
            <span v-html="item.title" />
          </VText>
          <VText
            tag="a"
            :href="`tel:${item.number.replace(/[\s-]/g, '')}`"
            :font="{
              xs: 'title-small',
              ml: 'title-large',
              l: 'title-medium',
            }"
            @click="onClickNumber(item)"
          >
            {{ item.number }}
          </VText>
        </VStackItem>
      </VStack>
    </VStackItem>
  </VStack>
  <VText
    tag="div"
    color="secondary"
    :font="{
      xs: 'caption-large-accent-low',
      ml: 'body-small-accent',
      xl: 'body-small-regular',
    }"
  >
    Круглосуточно, звонок по России бесплатный
  </VText>
  <div class="unit-footer-feedback">
    <a
      :href="linkHotline"
      rel="noreferrer"
      target="_blank"
      @click="send('Footer:Feedback:Form:Click')"
    >
      <VButton theme="invert" tag="span" class="unit-footer-feedback__btn"
        >Помощь</VButton
      >
    </a>
  </div>
</template>

<style lang="postcss" scoped>
.unit-footer-contacts {
  padding-top: 24px;

  & a {
    display: block;
    margin: 4px 0 12px;
    text-decoration: none;
  }
}

.unit-footer-feedback {
  margin: 16px 0;

  &__btn {
    width: 100%;
  }
}

@media (--pl-viewport-from-ml) {
  .unit-footer-contacts {
    & br {
      display: none;
    }
  }

  .unit-footer-feedback {
    &__btn {
      width: auto;
    }
  }
}

@media (--pl-viewport-from-l) {
  .unit-footer-contacts a:last-child {
    margin-bottom: 16px;
  }
}
</style>
